/**
 * La liaison temps reel, cote navigateur.
 *
 * Elle se deduit de l'adresse de la page (`location.host`): on joue depuis
 * localhost ou depuis le tailnet (100.110.158.75)
 * sans rien configurer, et sans second port a ouvrir.
 *
 * Ce module ne connait RIEN du jeu: il ouvre, il s'authentifie, il publie ce
 * qu'on lui donne et il rappelle ce qu'il recoit. Ce qu'il faut en faire est
 * decide dans client/jeu.ts.
 *
 * Le reseau d'une maison tombe: un portable qui se met en veille, un routeur
 * qui repart. La liaison se rouvre donc toute seule, avec une attente qui
 * s'allonge (mais qui reste bornee: on ne veut pas d'un jeu qui met deux
 * minutes a se rebrancher quand le wifi revient), et l'appelant est prevenu
 * pour rattraper ce qu'il a manque.
 */
import type { EtatPresence, EvenementMonde, JoueurConnecte, MessageChat } from '../../shared/types/reseau.ts'
import { CHEMIN_WS, VERSION_PROTOCOLE } from '../../shared/types/reseau.ts'
import { decoderServeur, encoder } from '../../shared/sim/reseau.ts'
import { journal } from '../log/journal.ts'

/** Attente avant la premiere tentative de reprise, en millisecondes. */
const ATTENTE_MIN_MS = 800
/** Plafond de l'attente. Au-dela, on ferait douter le joueur. */
const ATTENTE_MAX_MS = 12_000

/**
 * Delai au-dela duquel une tentative qui n'aboutit pas est abandonnee.
 *
 * Sans lui, une prise restee en CONNECTING ne declenche NI `onclose` NI
 * `onerror`: la seule relance programmee etait celle-la, donc plus rien
 * n'essayait. Mesure trois fois dans les journaux d'une vraie partie, toujours
 * la meme valeur: 130 secondes de silence avant que le navigateur ne renonce de
 * lui-meme. Huit secondes suffisent largement sur un reseau qui marche.
 */
const DELAI_OUVERTURE_MS = 8_000

export interface RappelsSalon {
  /** Liaison etablie. `reprise` est vrai si ce n'est pas la premiere fois. */
  onOuvert(id: string, joueurs: JoueurConnecte[], reprise: boolean, tempsJeu: number): void
  /**
   * LA LISTE COMPLETE, en reponse a `demanderQui()`. C'est le meme message que
   * l'accueil sur le fil, mais ce n'en est pas un ici: la liaison n'a pas
   * bouge, il n'y a ni horloge a reposer, ni monde a relire, ni « connexion
   * rétablie » a annoncer. Confondre les deux ferait dire au jeu qu'il vient de
   * se reconnecter chaque fois qu'il repare un trou dans sa liste.
   */
  onRoster(joueurs: JoueurConnecte[]): void
  /** L'heure du monde, tenue par le serveur. Le client la suit, il ne la pousse plus. */
  onHorloge(tempsJeu: number): void
  /** Les animaux du voisinage, simules une seule fois par le serveur. */
  onFaune(trame: number[]): void
  onArrive(joueur: JoueurConnecte): void
  onPart(id: string): void
  /** `tReception` est l'heure locale d'arrivee: c'est elle qui sert a lisser. */
  onPresence(joueurs: { id: string; e: EtatPresence }[], tReception: number): void
  /**
   * SA PROPRE position, telle que le serveur la connait, avec le numero de la
   * derniere entree prise en compte. C'est la moitie serveur de la
   * reconciliation (voir shared/sim/entree.ts).
   */
  onRecalage(n: number, x: number, z: number, rot: number): void
  /**
   * LE SERVEUR REFUSE LE VOLANT qu'on vient de prendre: quelqu'un y est deja,
   * ou le proprietaire a ferme a clef. Il faut descendre pour de bon, pas
   * seulement afficher un message — sinon on continue de tirer sur un vehicule
   * qui ne suit plus.
   */
  onVolantRefuse(id: string, raison: string): void
  onMonde(ev: EvenementMonde): void
  /** Des lignes de conversation: une qu'on vient de dire, ou l'historique. */
  onChat(messages: MessageChat[]): void
  /** Liaison perdue: plus personne n'est a jour tant qu'elle n'est pas revenue. */
  onFerme(): void
  /** Le serveur refuse: inutile d'insister (mode invite, version differente). */
  onRefus(raison: string): void
}

export class SalonClient {
  private readonly token: string
  private readonly rappels: RappelsSalon
  private prise: WebSocket | null = null
  private attente = ATTENTE_MIN_MS
  private minuterie = 0
  private minuterieOuverture = 0
  private arrete = false
  private dejaOuvert = false
  private ouvert = false
  /** Raison du refus du serveur, s'il a refuse. Aucune reprise ne l'effacera. */
  private raisonRefus: string | null = null

  constructor(token: string, rappels: RappelsSalon) {
    this.token = token
    this.rappels = rappels
    document.addEventListener('visibilitychange', this.surVisibilite)
  }

  /**
   * Pourquoi le serveur a refuse, ou null.
   *
   * Le distinguer d'une simple coupure est necessaire: une coupure se repare
   * toute seule, un refus jamais. Proposer « reconnexion... » a quelqu'un dont
   * la version est trop vieille, c'est le faire attendre pour rien.
   */
  get refus(): string | null {
    return this.raisonRefus
  }

  get connecte(): boolean {
    return this.ouvert
  }

  connecter(): void {
    if (this.arrete || this.prise) return
    const protocole = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const url = `${protocole}//${location.host}${CHEMIN_WS}`
    let prise: WebSocket
    try {
      prise = new WebSocket(url)
    } catch (e) {
      journal.warn('reseau', `ouverture impossible: ${String(e)}`)
      this.reprendrePlusTard()
      return
    }
    this.prise = prise

    // Une tentative qui n'aboutit pas doit RENONCER, sinon elle bloque la seule
    // relance programmee (voir DELAI_OUVERTURE_MS). Fermer la prise fait passer
    // par `onclose`, donc par le chemin de reprise habituel.
    clearTimeout(this.minuterieOuverture)
    this.minuterieOuverture = setTimeout(() => {
      if (this.prise !== prise || prise.readyState === WebSocket.OPEN) return
      journal.warn('reseau', `tentative sans reponse apres ${DELAI_OUVERTURE_MS} ms: on recommence`)
      prise.close()
    }, DELAI_OUVERTURE_MS) as unknown as number

    prise.onopen = () => {
      // Le JETON, jamais le mot de passe, et dans le corps du message plutot que
      // dans l'URL: une adresse se retrouve dans les journaux et les historiques.
      prise.send(encoder({ t: 'bonjour', version: VERSION_PROTOCOLE, token: this.token }))
    }

    prise.onmessage = (ev: MessageEvent) => {
      if (typeof ev.data !== 'string') return
      const m = decoderServeur(ev.data)
      if (!m) {
        journal.warn('reseau', 'message du serveur illisible')
        return
      }
      switch (m.t) {
        case 'bienvenue': {
          // Deja ouvert: c'est la reponse a une demande de liste, pas un
          // accueil. Le serveur envoie la meme forme pour les deux, et c'est
          // voulu — une seule fabrique cote serveur — mais ici tout differe.
          if (this.ouvert) {
            journal.info('reseau', 'liste des joueurs resynchronisee', { autres: m.joueurs.length })
            this.rappels.onRoster(m.joueurs)
            break
          }
          this.ouvert = true
          this.attente = ATTENTE_MIN_MS
          const reprise = this.dejaOuvert
          this.dejaOuvert = true
          journal.info('reseau', `liaison etablie${reprise ? ' (reprise)' : ''}`, {
            autres: m.joueurs.length,
          })
          this.rappels.onOuvert(m.id, m.joueurs, reprise, m.tempsJeu)
          break
        }
        case 'horloge':
          this.rappels.onHorloge(m.tempsJeu)
          break
        case 'faune':
          // Volontairement sans journal: huit trames par seconde rempliraient le
          // fichier de session, qui doit rester lisible par un humain.
          this.rappels.onFaune(m.a)
          break
        case 'refus':
          journal.info('reseau', `liaison refusee: ${m.raison}`)
          this.arrete = true
          this.raisonRefus = m.raison
          this.rappels.onRefus(m.raison)
          break
        case 'arrive':
          journal.info('reseau', `${m.joueur.nom} est arrive`, { compte: m.joueur.compte })
          this.rappels.onArrive(m.joueur)
          break
        case 'part':
          journal.info('reseau', 'un joueur est parti', { id: m.id })
          this.rappels.onPart(m.id)
          break
        case 'presence':
          this.rappels.onPresence(m.joueurs, performance.now())
          break
        case 'recalage':
          // Volontairement sans journal: dix par seconde rempliraient le
          // fichier de session. Ce qui merite d'etre ecrit — un ecart qu'il a
          // fallu corriger d'un saut — l'est par client/jeu.ts.
          this.rappels.onRecalage(m.n, m.x, m.z, m.rot)
          break
        case 'volant':
          journal.warn('vehicule', `volant refuse par le serveur: ${m.raison}`, { id: m.id })
          this.rappels.onVolantRefuse(m.id, m.raison)
          break
        case 'monde':
          journal.info('reseau', `changement du monde recu: ${m.ev.quoi}`)
          this.rappels.onMonde(m.ev)
          break
        case 'chat':
          // Le contenu est journalise par `client/ui/chat.ts`, une ligne par
          // message: le relire ici le ferait apparaitre deux fois dans le
          // fichier de session, une fois brut et une fois affiche.
          this.rappels.onChat(m.messages)
          break
      }
    }

    prise.onerror = () => {
      // `onclose` suit toujours: on ne fait rien ici, sinon on compterait deux
      // fois la meme panne et l'attente doublerait trop vite.
      journal.debug('reseau', 'erreur de la liaison')
    }

    prise.onclose = (ev: CloseEvent) => {
      if (this.prise !== prise) return
      this.prise = null
      clearTimeout(this.minuterieOuverture)
      const etaitOuvert = this.ouvert
      this.ouvert = false
      if (etaitOuvert) {
        journal.warn('reseau', `liaison perdue (code ${ev.code})`)
        this.rappels.onFerme()
      }
      this.reprendrePlusTard()
    }
  }

  /**
   * Reprend la main des que l'onglet redevient visible.
   *
   * C'est la moitie manquante de la reprise. Chrome sur Android GELE un onglet
   * cache au bout de cinq minutes — la correlation est exacte dans les journaux,
   * quatre fois de suite: « onglet masque », puis la liaison tombe a 5 min 00 s.
   * Les tentatives qui suivent echouent toutes tant que l'onglet dort, et la
   * premiere lancee apres le reveil reussit. On attendait donc que le hasard du
   * minuteur tombe apres le retour du joueur, ce qui coutait jusqu'a douze
   * secondes de monde fige pour rien.
   */
  private surVisibilite = (): void => {
    if (this.arrete || document.visibilityState !== 'visible' || this.ouvert) return
    journal.info('reseau', 'onglet revenu: reprise immediate')
    clearTimeout(this.minuterie)
    this.attente = ATTENTE_MIN_MS
    if (!this.prise) this.connecter()
  }

  /** Publie sa propre position. Sans liaison, on laisse tomber en silence. */
  publier(e: EtatPresence): void {
    if (!this.ouvert || !this.prise || this.prise.readyState !== WebSocket.OPEN) return
    this.prise.send(encoder({ t: 'etat', e }))
  }

  /**
   * Envoie un lot d'entrees. Sans liaison, on laisse tomber en silence.
   *
   * Ce qui est perdu est perdu: on ne rejoue pas les entrees d'une coupure au
   * retour du wifi. Le serveur recalerait le joueur sur un deplacement vieux de
   * plusieurs secondes, alors qu'il a continue de jouer pendant ce temps-la.
   */
  publierEntrees(e: number[]): void {
    if (e.length === 0) return
    if (!this.ouvert || !this.prise || this.prise.readyState !== WebSocket.OPEN) return
    this.prise.send(encoder({ t: 'entrees', e }))
  }

  /**
   * Dit quelque chose a tout le monde. Rend faux si la liaison est coupee, pour
   * que l'interface puisse le dire au joueur au lieu d'avaler sa phrase.
   */
  dire(texte: string): boolean {
    if (!this.ouvert || !this.prise || this.prise.readyState !== WebSocket.OPEN) return false
    this.prise.send(encoder({ t: 'dire', texte }))
    return true
  }

  /**
   * Redemande la liste complete des connectes.
   *
   * A n'appeler que sur un SYMPTOME — une position recue d'un identifiant
   * inconnu — et en se freinant: le symptome se repete dix fois par seconde
   * tant que le trou n'est pas bouche, et le remede ne doit pas couter plus
   * cher que la panne.
   */
  demanderQui(): boolean {
    if (!this.ouvert || !this.prise || this.prise.readyState !== WebSocket.OPEN) return false
    this.prise.send(encoder({ t: 'qui' }))
    return true
  }

  /**
   * Demande au serveur d'avancer jusqu'a l'aube. Rend faux si la liaison est
   * coupee: l'appelant sait alors qu'il doit se debrouiller tout seul.
   */
  demanderAube(): boolean {
    if (!this.ouvert || !this.prise || this.prise.readyState !== WebSocket.OPEN) return false
    this.prise.send(encoder({ t: 'dormir' }))
    return true
  }

  private reprendrePlusTard(): void {
    if (this.arrete) return
    clearTimeout(this.minuterie)
    // Un peu de hasard: si le routeur repart, trois joueurs ne doivent pas
    // frapper a la porte exactement au meme instant.
    const delai = this.attente * (0.8 + Math.random() * 0.4)
    this.minuterie = setTimeout(() => this.connecter(), delai) as unknown as number
    this.attente = Math.min(ATTENTE_MAX_MS, this.attente * 2)
    journal.debug('reseau', `nouvelle tentative dans ${Math.round(delai)} ms`)
  }

  /**
   * Coupe une liaison PRESUMEE MORTE et enclenche la reprise tout de suite.
   *
   * Une prise TCP a moitie morte — l'aval marche, l'amont ne passe plus — ne
   * declenche jamais `onclose`: le serveur nous a deja lache pour « silence
   * prolonge » pendant que le navigateur se croit connecte. Mesure dans une
   * vraie partie: 97 secondes a jouer dans le vide avant le code 1006. On
   * n'attend pas non plus le `onclose` de `close()` — sur cette prise-la, la
   * poignee de fermeture peut mettre une minute — on debranche les rappels et
   * on suit a la main le chemin exact du `onclose` normal: voile, puis reprise.
   */
  couper(raison: string): void {
    const p = this.prise
    if (!p) return
    this.prise = null
    p.onclose = null
    p.onmessage = null
    p.onerror = null
    try {
      p.close(4001, raison)
    } catch {
      // La liaison etait deja tombee: il n'y a rien a fermer.
    }
    clearTimeout(this.minuterieOuverture)
    const etaitOuvert = this.ouvert
    this.ouvert = false
    this.attente = ATTENTE_MIN_MS
    if (etaitOuvert) {
      journal.warn('reseau', `liaison coupee par le client: ${raison}`)
      this.rappels.onFerme()
    }
    this.reprendrePlusTard()
  }

  /**
   * Coupe la liaison SANS renoncer: la reprise automatique s'enclenche.
   *
   * Passerelle de test. Debrancher vraiment le wifi au milieu d'un test de bout
   * en bout n'est pas possible; couper la liaison par en dessous emprunte
   * exactement le meme chemin de code que le routeur qui repart.
   */
  couperPourTest(attenteMs = ATTENTE_MIN_MS): void {
    // On choisit la duree de la panne: sans cela, la reprise arrive en huit
    // dixiemes de seconde et le test ne verifie plus le RATTRAPAGE, seulement
    // que la liaison revient.
    this.attente = Math.max(ATTENTE_MIN_MS, attenteMs)
    this.prise?.close(4000, 'coupure simulee')
  }

  arreter(): void {
    this.arrete = true
    clearTimeout(this.minuterie)
    clearTimeout(this.minuterieOuverture)
    document.removeEventListener('visibilitychange', this.surVisibilite)
    const p = this.prise
    this.prise = null
    this.ouvert = false
    if (p) {
      p.onclose = null
      p.onmessage = null
      p.onerror = null
      try {
        p.close(1000, 'depart')
      } catch {
        // La liaison etait deja tombee: il n'y a rien a fermer.
      }
    }
  }
}
